import { useState, useEffect, useRef } from 'react';
import { Form, Button, Input, InputNumber, Space, Notification, Select, Typography, Card, TagInput, Tabs, Modal, Table, Tag } from '@douyinfe/semi-ui';
import { IconUpload, IconInfoCircle, IconImage, IconEdit, IconSetting, IconClose, IconPriceTag, IconList } from '@douyinfe/semi-icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import request from '../../utils/request';
import ImageSelector from '../../components/ImageSelector';
import RichEditor from '../../components/RichEditor';
import ProductCategorySelector from '../../components/ProductCategorySelector';
import styles from './edit.module.scss';

const { Title } = Typography;
const { TabPane } = Tabs;

const ProductEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tagsFromUrl = searchParams.get('tags');
    const tagsInt = tagsFromUrl ? parseInt(tagsFromUrl, 10) : undefined;
    const [loading, setLoading] = useState(false);
    const formApi = useRef(null);
    const [productImages, setProductImages] = useState([]);
    const [activeTab, setActiveTab] = useState('basic');
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [productAttrs, setProductAttrs] = useState([]);
    const [productSkus, setProductSkus] = useState([]);
    const [skuModalVisible, setSkuModalVisible] = useState(false);
    const [currentSku, setCurrentSku] = useState(null);
    const [skuFormApi, setSkuFormApi] = useState(null);
    const [tags, setTags] = useState();

    useEffect(() => {
        if (id) {
            fetchProductDetail();
            fetchProductAttrs();
            fetchProductSkus();
        } else {
            // 新增商品时，如果有tags参数，设置到表单
            if (tagsInt !== undefined && !isNaN(tagsInt)) {
                setTags(tagsInt);
            }
        }
    }, [id]);

    const fetchProductDetail = async () => {
        setLoading(true);
        try {
            const res = await request.get('/go/sp/shop/get_product/', {
                params: { uid: id }
            });
            if (res.errno === 0 && res.data) {
                console.log('后端返回的商品数据:', res.data);
                console.log('后端返回的cat_uid:', res.data.cat_uid);

                const formData = {
                    ...res.data,
                    price: res.data.price ? res.data.price / 100 : 0,
                    ori_price: res.data.ori_price ? res.data.ori_price / 100 : 0,
                    cat_uid: res.data.cat_uid || '',
                    content: {
                        html: res.data.content?.html || '',
                        images: res.data.content?.images || [],
                        services: Array.isArray(res.data.content?.services)
                            ? res.data.content.services
                            : (res.data.content?.services || '').split(',').filter(Boolean),
                        assoc_uids: res.data.content?.assoc_uids || []
                    }
                };
                console.log('处理后的表单数据:', formData);
                setProductImages(formData.content.images || []);
                setTags(res.data.tags); // 新增

                if (formData.main_img) {
                    formApi.current?.setValue('main_img', formData.main_img);
                }

                // 先设置所有基本字段
                const { main_img, content, ...restData } = formData;
                console.log('准备设置的表单数据:', restData);
                formApi.current?.setValues(restData);

                // 单独设置 cat_uid
                if (formData.cat_uid) {
                    console.log('单独设置 cat_uid:', formData.cat_uid);
                    formApi.current?.setValue('cat_uid', formData.cat_uid);
                }

                // 单独设置 content 字段
                if (content) {
                    Object.keys(content).forEach(key => {
                        formApi.current?.setValue(`content.${key}`, content[key]);
                    });
                }

                // 检查表单值是否正确设置
                const formValues = formApi.current?.getValues();
                console.log('设置后的表单值:', formValues);
                console.log('cat_uid的值:', formValues?.cat_uid);
            } else {
                Notification.error({
                    title: '获取商品详情失败',
                    content: res.errstr || '请稍后重试'
                });
            }
        } catch (error) {
            Notification.error({
                title: '获取商品详情失败',
                content: '请稍后重试'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchProductAttrs = async () => {
        try {
            const res = await request.get('/go/sp/shop/get_product_attr_vals/', {
                params: { product_uid: id }
            });
            console.log('获取到的属性数据:', res);
            if (res.errno === 0 && res.avs) {
                const attrs = res.avs || [];
                console.log('原始属性数据:', attrs);
                // 确保每个属性都有vals数组
                const processedAttrs = attrs.map(attr => ({
                    name: attr.name || '',
                    tags: attr.tags || 0,
                    vals: (attr.vals || []).map(val => ({
                        name: val.name || '',
                        image: val.image || ''
                    }))
                }));
                console.log('处理后的属性数据:', processedAttrs);
                setProductAttrs(processedAttrs);
            }
        } catch (error) {
            console.error('获取商品属性失败:', error);
            Notification.error({
                title: '获取商品属性失败',
                content: '请稍后重试'
            });
        }
    };

    // 添加一个useEffect来监听productAttrs的变化
    useEffect(() => {
        console.log('当前productAttrs状态:', productAttrs);
    }, [productAttrs]);

    const handleAddAttr = () => {
        const newAttrs = [...productAttrs, { name: '', vals: [], tags: 0 }];
        console.log('添加新属性后的数据:', newAttrs);
        setProductAttrs(newAttrs);
    };

    const handleDeleteAttr = (index) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个属性吗？删除后该属性的所有值也将被删除。',
            onOk: () => {
                const newAttrs = [...productAttrs];
                newAttrs.splice(index, 1);
                setProductAttrs(newAttrs);
            }
        });
    };

    const handleAddAttrVal = (attrIndex) => {
        const newAttrs = [...productAttrs];
        if (!newAttrs[attrIndex].vals) {
            newAttrs[attrIndex].vals = [];
        }
        newAttrs[attrIndex].vals = [...newAttrs[attrIndex].vals, { name: '', image: '' }];
        console.log('添加新属性值后的数据:', newAttrs);
        setProductAttrs(newAttrs);
    };

    const handleDeleteAttrVal = (attrIndex, valIndex) => {
        const newAttrs = [...productAttrs];
        newAttrs[attrIndex].vals.splice(valIndex, 1);
        setProductAttrs(newAttrs);
    };

    const handleAttrChange = (attrIndex, field, value) => {
        const newAttrs = [...productAttrs];
        newAttrs[attrIndex][field] = value;
        console.log('修改属性后的数据:', newAttrs);
        setProductAttrs(newAttrs);
    };

    const handleAttrValChange = (attrIndex, valIndex, field, value) => {
        const newAttrs = [...productAttrs];
        newAttrs[attrIndex].vals[valIndex][field] = value;
        console.log('修改属性值后的数据:', newAttrs);
        setProductAttrs(newAttrs);
    };

    const handleSubmit = async (values) => {
        // 表单提交前的验证
        const fieldLabels = {
            title: '商品标题',
            price: '价格',
            quantity: '库存'
        };

        const basicFields = ['title', 'price', 'quantity'];
        const missingFields = basicFields.filter(field => {
            if (field === 'quantity') {
                // 库存允许为0，但必须填写（不能为undefined或null）
                return values[field] === undefined || values[field] === null || values[field] === '';
            }
            return !values[field];
        });

        if (missingFields.length > 0) {
            setActiveTab('basic');
            const missingFieldNames = missingFields.map(field => fieldLabels[field]).join('、');
            Notification.warning({
                title: '请完善基本信息',
                content: `请填写以下必填项：${missingFieldNames}`
            });
            return;
        }

        if (!values.main_img) {
            setActiveTab('images');
            Notification.warning({
                title: '请上传商品主图',
                content: '商品主图为必填项'
            });
            return;
        }

        setLoading(true);
        try {
            const api = id ? '/go/sp/shop/edit_product/' : '/go/sp/shop/add_product/';
            const data = {
                ...values,
                tags, // 手动加上tags
                price: Math.round(values.price * 100),
                ori_price: Math.round(values.ori_price * 100),
                content: {
                    ...values.content,
                    images: productImages,
                    services: Array.isArray(values.content?.services)
                        ? values.content.services
                        : [],
                    assoc_uids: Array.isArray(values.content?.assoc_uids)
                        ? values.content.assoc_uids
                        : values.content?.assoc_uids?.split(',').filter(Boolean) || []
                }
            };
            if (id) {
                data.uid = id;
            }

            const res = await request.post(api, data);
            if (res.errno === 0) {
                // 保存商品属性
                if (id) {
                    await request.post('/go/sp/shop/set_product_attr_vals/', {
                        product_uid: id,
                        avs: productAttrs
                    });
                }
                Notification.success({
                    title: `${id ? '编辑' : '创建'}成功`,
                    content: `商品已成功${id ? '编辑' : '创建'}`
                });
                navigate(-1);
            } else {
                Notification.error({
                    title: `${id ? '编辑' : '创建'}失败`,
                    content: res.errstr || '请稍后重试'
                });
            }
        } catch (error) {
            Notification.error({
                title: `${id ? '编辑' : '创建'}失败`,
                content: '请稍后重试'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleProductImagesChange = (images) => {
        setProductImages(images);
        formApi.current?.setValue('content.images', images);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(productImages);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        handleProductImagesChange(items);
    };

    const handleDeleteConfirm = (index) => {
        setDeleteIndex(index);
    };

    const handleDelete = () => {
        const newImages = productImages.filter((_, i) => i !== deleteIndex);
        handleProductImagesChange(newImages);
        setDeleteIndex(null);
    };

    const fetchProductSkus = async () => {
        try {
            const res = await request.get('/go/sp/shop/list_product_sku/', {
                params: { product_uid: id }
            });
            if (res.errno === 0) {
                setProductSkus(res.list || []);
            }
        } catch (error) {
            console.error('获取商品SKU失败:', error);
            Notification.error({
                title: '获取商品SKU失败',
                content: '请稍后重试'
            });
        }
    };

    const handleEditSku = (sku) => {
        setCurrentSku(sku);
        setSkuModalVisible(true);
    };

    const handleDeleteSku = (sku) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个SKU吗？',
            onOk: async () => {
                try {
                    const res = await request.post('/go/sp/shop/delete_product_sku/', {
                        uids: sku.uid
                    });
                    if (res.errno === 0) {
                        Notification.success({
                            title: '删除成功',
                            content: 'SKU已成功删除'
                        });
                        fetchProductSkus();
                    }
                } catch (error) {
                    Notification.error({
                        title: '删除失败',
                        content: '请稍后重试'
                    });
                }
            }
        });
    };

    const handleSkuSubmit = async (values) => {
        try {
            const data = {
                ...values,
                product_uid: id,
                price: Math.round(values.price * 100),
                ori_price: Math.round(values.ori_price * 100)
            };

            if (currentSku?.uid) {
                data.uid = currentSku.uid;
                await request.post('/go/sp/shop/edit_product_sku/', data);
            } else {
                await request.post('/go/sp/shop/add_product_sku/', data);
            }

            Notification.success({
                title: currentSku?.uid ? '编辑成功' : '添加成功',
                content: `SKU已成功${currentSku?.uid ? '编辑' : '添加'}`
            });
            setSkuModalVisible(false);
            fetchProductSkus();
        } catch (error) {
            Notification.error({
                title: currentSku?.uid ? '编辑失败' : '添加失败',
                content: '请稍后重试'
            });
        }
    };

    const generateSkuCombinations = () => {
        if (productAttrs.length === 0) {
            Notification.warning({
                title: '请先添加商品属性',
                content: '需要先添加商品属性才能生成SKU组合'
            });
            return;
        }

        // 获取当前商品的价格
        const productPrice = formApi.current?.getValue('price') || 0;
        const productOriPrice = formApi.current?.getValue('ori_price') || 0;

        // 生成所有可能的属性组合
        const combinations = [];
        const generateCombinations = (current, index) => {
            if (index === productAttrs.length) {
                combinations.push(current);
                return;
            }

            const attr = productAttrs[index];
            if (attr.vals && attr.vals.length > 0) {
                attr.vals.forEach(val => {
                    generateCombinations([...current, `${attr.name}:${val.name}`], index + 1);
                });
            }
        };

        generateCombinations([], 0);

        // 获取已存在的SKU组合
        const existingCombinations = new Set(productSkus.map(sku => sku.sku_uid));

        // 为每个组合创建SKU，跳过已存在的组合
        const newSkus = combinations
            .filter(comb => {
                const skuUid = `${id};${comb.join(';')}`;
                return !existingCombinations.has(skuUid);
            })
            .map(comb => {
                const skuUid = `${id};${comb.join(';')}`;
                return {
                    product_uid: id,
                    sku_uid: skuUid,
                    sku_code: '', // 不自动生成SKU编码
                    price: Math.round(productPrice * 100), // 使用商品价格
                    ori_price: Math.round(productOriPrice * 100), // 使用商品原价
                    quantity: 0,
                    status: 0,
                    image: '' // 添加图片字段
                };
            });

        if (newSkus.length === 0) {
            Notification.info({
                title: '没有新的SKU组合',
                content: '所有可能的属性组合已经存在'
            });
            return;
        }

        setProductSkus([...productSkus, ...newSkus]);
        Notification.success({
            title: '生成成功',
            content: `已生成${newSkus.length}个新的SKU组合`
        });
    };

    const handleBatchSaveSkus = async () => {
        const newSkus = productSkus.filter(sku => !sku.uid);
        if (newSkus.length === 0) {
            Notification.info({
                title: '没有需要保存的SKU',
                content: '所有SKU已经保存'
            });
            return;
        }

        try {
            for (const sku of newSkus) {
                const res = await request.post('/go/sp/shop/add_product_sku/', {
                    product_uid: id,
                    ...sku
                });

                if (res.errno !== 0) {
                    throw new Error(res.errstr || '保存失败');
                }
            }

            Notification.success({
                title: '保存成功',
                content: `已成功保存${newSkus.length}个SKU`
            });
            fetchProductSkus();
        } catch (error) {
            Notification.error({
                title: '保存失败',
                content: error.message || '请稍后重试'
            });
        }
    };

    const skuColumns = [
        {
            title: 'SKU编码',
            dataIndex: 'sku_code',
            width: 200,
            render: (text, record) => (
                <div>
                    <div>{text || '-'}</div>
                    <div style={{ color: 'var(--semi-color-text-2)', fontSize: '12px' }}>
                        {record.sku_uid.split(';').slice(1).map(attr => attr.split(':')[1]).join(' / ')}
                    </div>
                    {record.buy_limit > 0 && (
                        <div style={{ color: 'var(--semi-color-text-2)', fontSize: '12px' }}>
                            限购: {record.buy_limit}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: '图片',
            dataIndex: 'image',
            width: 80,
            render: (text) => (
                text ? (
                    <img
                        src={text}
                        alt="SKU图片"
                        style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                ) : '-'
            )
        },
        {
            title: '价格',
            dataIndex: 'price',
            width: 100,
            render: (text) => `¥${(text / 100).toFixed(2)}`
        },
        {
            title: '库存',
            dataIndex: 'quantity',
            width: 120,
            render: (text, record) => (
                <Space>
                    <span>{text}</span>
                    {record.quantity_warn > 0 && text <= record.quantity_warn && (
                        <span style={{ color: 'var(--semi-color-danger)' }}>库存预警</span>
                    )}
                </Space>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            render: (text) => text === 0 ? '正常' : '下架'
        },
        {
            title: '操作',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button type="primary" onClick={() => handleEditSku(record)}>编辑</Button>
                    <Button type="danger" onClick={() => handleDeleteSku(record)}>删除</Button>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.productEdit}>
            <Card>
                <Title heading={3} style={{ marginBottom: 24 }}>
                    {id ? '编辑商品' : '新增商品'}
                </Title>
                <Form
                    getFormApi={api => formApi.current = api}
                    onSubmit={handleSubmit}
                    labelPosition="left"
                    labelWidth={120}
                    labelAlign="right"
                    initValues={!id && tagsInt !== undefined && !isNaN(tagsInt) ? { tags: tagsInt } : {}}
                >
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        tabBarStyle={{ marginBottom: 32 }}
                    >
                        <TabPane
                            tab={
                                <span>
                                    <IconInfoCircle style={{ marginRight: 8 }} />
                                    基本信息
                                </span>
                            }
                            itemKey="basic"
                        >
                            <div className={styles.formSection}>
                                <Form.Input
                                    field="title"
                                    label="商品标题"
                                    rules={[{ required: true, message: '请输入商品标题' }]}
                                />
                                <Form.Input
                                    field="title2"
                                    label="副标题"
                                />
                                <Form.Slot label="商品分类" field="cat_uid" rules={[{ required: true, message: '请选择商品分类' }]}>
                                    <ProductCategorySelector
                                        value={formApi.current?.getValue('cat_uid')}
                                        onChange={(value) => {
                                            console.log('分类选择变化:', value);
                                            formApi.current?.setValue('cat_uid', value);
                                            console.log('设置后的表单值:', formApi.current?.getValues());
                                        }}
                                    />
                                </Form.Slot>
                                <div className={styles.formRow}>
                                    <Form.InputNumber
                                        field="price"
                                        label="价格(元)"
                                        rules={[{ required: true, message: '请输入商品价格' }]}
                                        precision={2}
                                    />
                                    <Form.InputNumber
                                        field="ori_price"
                                        label="原价(元)"
                                        precision={2}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <Form.InputNumber
                                        field="quantity"
                                        label="库存"
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入库存数量',
                                                validator: (rule, value) => {
                                                    if (value === undefined || value === null || value === '') {
                                                        return Promise.reject('请输入库存数量');
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                        min={0}
                                    />
                                    <Form.InputNumber
                                        field="quantity_warn"
                                        label="库存告警"
                                        min={0}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <Form.Select
                                        field="status"
                                        label="商品状态"
                                        initValue={0}
                                    >
                                        <Form.Select.Option value={0}>正常</Form.Select.Option>
                                        <Form.Select.Option value={1}>下架</Form.Select.Option>
                                    </Form.Select>
                                    <Form.InputNumber
                                        field="buy_limit"
                                        label="限购数量"
                                        min={0}
                                    />
                                </div>
                            </div>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <IconImage style={{ marginRight: 8 }} />
                                    商品图片
                                </span>
                            }
                            itemKey="images"
                        >
                            <div className={styles.formSection}>
                                <Form.Slot label="商品主图" field="main_img" rules={[{ required: true, message: '请上传商品主图' }]}>
                                    <div style={{ marginBottom: 8, color: 'var(--semi-color-text-2)' }}>
                                        建议图片尺寸：800*800像素
                                    </div>
                                    <ImageSelector
                                        value={formApi.current?.getValue('main_img')}
                                        onChange={(url) => {
                                            formApi.current?.setValue('main_img', url);
                                        }}
                                    />
                                </Form.Slot>
                                <Form.Slot label="更多图片" field="content.images">
                                    <div style={{ marginBottom: 8, color: 'var(--semi-color-text-2)' }}>
                                        建议图片尺寸：800*800像素
                                    </div>
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="productImages" direction="horizontal">
                                            {(provided) => (
                                                <div
                                                    className={styles.multiImageSelector}
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {productImages.map((img, index) => (
                                                        <Draggable key={index} draggableId={`img-${index}`} index={index}>
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={styles.imageItem}
                                                                >
                                                                    <ImageSelector
                                                                        value={img}
                                                                        onChange={(url) => {
                                                                            const newImages = [...productImages];
                                                                            newImages[index] = url;
                                                                            handleProductImagesChange(newImages);
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        type="danger"
                                                                        theme="borderless"
                                                                        icon={<IconClose />}
                                                                        onClick={() => handleDeleteConfirm(index)}
                                                                        className={styles.deleteButton}
                                                                    />
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                    <Button
                                                        icon={<IconUpload />}
                                                        onClick={() => handleProductImagesChange([...productImages, ''])}
                                                        disabled={productImages.length >= 10}
                                                    >
                                                        添加图片
                                                    </Button>
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </Form.Slot>
                                <Form.Input
                                    field="video_url"
                                    label="视频地址"
                                    placeholder="请输入商品视频地址"
                                />
                            </div>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <IconEdit style={{ marginRight: 8 }} />
                                    商品详情
                                </span>
                            }
                            itemKey="detail"
                        >
                            <div className={styles.formSection}>
                                <div style={{ width: '100%' }}>
                                    <RichEditor
                                        value={formApi.current?.getValue('content.html')}
                                        onChange={(value) => formApi.current?.setValue('content.html', value)}
                                        style={{ width: '100%', height: '600px' }}
                                    />
                                </div>
                            </div>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <IconSetting style={{ marginRight: 8 }} />
                                    其他设置
                                </span>
                            }
                            itemKey="settings"
                        >
                            <div className={styles.formSection}>
                                <div className={styles.formRow}>
                                    <Form.InputNumber
                                        field="back_point"
                                        label="返还积分"
                                        min={0}
                                    />
                                    <Form.InputNumber
                                        field="sort"
                                        label="排序"
                                        initValue={0}
                                    />
                                    <div style={{ marginTop: -8, color: 'var(--semi-color-text-2)', fontSize: '12px' }}>
                                        数字越大排序越靠前
                                    </div>
                                </div>
                                <Form.TagInput
                                    field="content.services"
                                    label="服务内容"
                                    placeholder="输入服务内容后按回车添加"
                                    separator={[',']}
                                    maxTagCount={10}
                                />
                                <Form.Input
                                    field="content.assoc_uids"
                                    label="关联商品"
                                    placeholder="多个商品ID用逗号分隔"
                                />
                            </div>
                        </TabPane>

                        {id && (
                            <TabPane
                                tab={
                                    <span>
                                        <IconPriceTag style={{ marginRight: 8 }} />
                                        商品属性
                                    </span>
                                }
                                itemKey="attributes"
                            >
                                <div className={styles.formSection}>
                                    <div className={styles.attrList}>
                                        {productAttrs.map((attr, attrIndex) => (
                                            <div key={attrIndex} className={styles.attrItem}>
                                                <div className={styles.attrHeader}>
                                                    <Input
                                                        value={attr.name || ''}
                                                        onChange={value => handleAttrChange(attrIndex, 'name', value)}
                                                        placeholder="请输入属性名称（如：颜色、尺寸）"
                                                        style={{ width: 250 }}
                                                    />
                                                    <Space>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => handleAddAttrVal(attrIndex)}
                                                        >
                                                            添加属性值
                                                        </Button>
                                                        <Button
                                                            type="danger"
                                                            onClick={() => handleDeleteAttr(attrIndex)}
                                                        >
                                                            删除属性
                                                        </Button>
                                                    </Space>
                                                </div>
                                                <div className={styles.attrValues}>
                                                    {(attr.vals || []).map((val, valIndex) => (
                                                        <div key={valIndex} className={styles.attrValueItem}>
                                                            <Input
                                                                value={val.name || ''}
                                                                onChange={value => handleAttrValChange(attrIndex, valIndex, 'name', value)}
                                                                placeholder="请输入属性值"
                                                                style={{ width: 150 }}
                                                            />
                                                            <ImageSelector
                                                                value={val.image || ''}
                                                                onChange={value => handleAttrValChange(attrIndex, valIndex, 'image', value)}
                                                                style={{ marginLeft: 8 }}
                                                                previewSize={48}
                                                            />
                                                            <Button
                                                                type="danger"
                                                                theme="borderless"
                                                                icon={<IconClose />}
                                                                onClick={() => handleDeleteAttrVal(attrIndex, valIndex)}
                                                                style={{ marginLeft: 8 }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        <Button onClick={handleAddAttr} style={{ marginTop: 16 }}>
                                            添加属性
                                        </Button>
                                    </div>
                                </div>
                            </TabPane>
                        )}

                        {id && (
                            <TabPane
                                tab={
                                    <span>
                                        <IconList style={{ marginRight: 8 }} />
                                        SKU管理
                                    </span>
                                }
                                itemKey="sku"
                            >
                                <div className={styles.formSection}>
                                    <Space style={{ marginBottom: 16 }}>
                                        <Button type="primary" onClick={generateSkuCombinations}>
                                            根据属性生成SKU
                                        </Button>
                                        {productSkus.some(sku => !sku.uid) && (
                                            <Button type="primary" onClick={handleBatchSaveSkus}>
                                                保存新生成的SKU
                                            </Button>
                                        )}
                                    </Space>
                                    <Table
                                        columns={skuColumns}
                                        dataSource={productSkus}
                                        pagination={false}
                                    />
                                </div>
                            </TabPane>
                        )}
                    </Tabs>

                    <div className={styles.formActions}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading} theme="solid" size="large">
                                {id ? '保存修改' : '创建商品'}
                            </Button>
                            <Button size="large" onClick={() => navigate(-1)}>取消</Button>
                        </Space>
                    </div>
                </Form>
            </Card>

            <Modal
                title="编辑SKU"
                visible={skuModalVisible}
                onCancel={() => setSkuModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    getFormApi={setSkuFormApi}
                    onSubmit={handleSkuSubmit}
                    labelPosition="left"
                    labelWidth={120}
                    labelAlign="right"
                    initValues={currentSku ? {
                        ...currentSku,
                        price: currentSku.price / 100,
                        ori_price: currentSku.ori_price / 100
                    } : {}}
                >
                    <Form.Input
                        field="sku_code"
                        label="SKU编码"
                        placeholder="选填"
                    />
                    {currentSku && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ color: 'var(--semi-color-text-2)', marginBottom: 8 }}>属性组合</div>
                            <div>
                                {currentSku.sku_uid.split(';').slice(1).map((attr, index) => (
                                    <Tag key={index} style={{ marginRight: 8, marginBottom: 8 }}>
                                        {attr}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                    <Form.Slot label="SKU图片" field="image">
                        <ImageSelector
                            value={currentSku?.image}
                            onChange={(url) => {
                                skuFormApi?.setValue('image', url);
                            }}
                        />
                    </Form.Slot>
                    <Form.InputNumber
                        field="price"
                        label="价格(元)"
                        rules={[{ required: true, message: '请输入价格' }]}
                        precision={2}
                    />
                    <Form.InputNumber
                        field="ori_price"
                        label="原价(元)"
                        precision={2}
                    />
                    <Form.InputNumber
                        field="quantity"
                        label="库存"
                        rules={[{ required: true, message: '请输入库存' }]}
                        min={0}
                    />
                    <Form.InputNumber
                        field="quantity_warn"
                        label="库存告警"
                        min={0}
                    />
                    <Form.InputNumber
                        field="back_point"
                        label="返还积分"
                        min={0}
                    />
                    <Form.InputNumber
                        field="buy_limit"
                        label="限购数量"
                        min={0}
                    />
                    <Form.InputNumber
                        field="volume"
                        label="体积(cm³)"
                        min={0}
                    />
                    <Form.InputNumber
                        field="weight"
                        label="重量(g)"
                        min={0}
                    />
                    <Form.Select
                        field="status"
                        label="状态"
                        initValue={0}
                    >
                        <Form.Select.Option value={0}>正常</Form.Select.Option>
                        <Form.Select.Option value={1}>下架</Form.Select.Option>
                    </Form.Select>
                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button type="tertiary" onClick={() => setSkuModalVisible(false)}>取消</Button>
                            <Button type="primary" htmlType="submit">确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="确认删除"
                visible={deleteIndex !== null}
                onOk={handleDelete}
                onCancel={() => setDeleteIndex(null)}
                okType="danger"
            >
                <p>确定要删除这张图片吗？</p>
            </Modal>
        </div>
    );
};

export default ProductEdit;
